import Icon, { IconType } from 'components/Icon';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import { getBoundingBox } from 'dragonmouse-shared/geometry';
import {
	Attachment,
	ChatEntry,
	ChatEntryRestriction,
	ChatStatus,
	FRAME_GAP,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { isEqual } from 'lodash';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveChat, selectActiveScene, selectActiveStyle } from 'redux/db/reducers';
import {
	ChatMessageFramesPart,
	ChatMessageTextPart,
	getFrameBounds,
	getFrames,
	parseFramesXml,
	splitMessageWithFrames,
} from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setFrameIdToScrollTo,
	setPendingChatInput,
	showContextMenu,
} from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { FileUploadCallbacks, onFileDropped } from 'utils';
import { useTheme } from 'styled-components';
import ChatInput from './ChatInput';
import * as S from './styles';

const Conversation = () => {
	const containerRef = useRef<HTMLDivElement>(null);

	// spacer div we will use to force user's last input to scroll to the top of their screen when they submit a prompt
	const spacerRef = useRef<HTMLDivElement>(null);
	// we store these heights in refs to avoid unnecessary re-renders
	const originalSpacerHeight = useRef<number>(0);
	const spacerHeight = useRef<number>(0);

	const dispatch = useDispatch();
	const theme = useTheme();
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const [focusedFrameIds, setFocusedFrameIds] = useState<string[]>([]);
	const [isDraggingFile, setIsDraggingFile] = useState(false);

	const {
		panelsVisible,
		activeChat,
		frameIdToScrollTo,
		centerFramesAsChatGeneratesThem,
		scene,
		isMobile,
		pendingChatInput,
	} = useSelector((r: RootReducer) => {
		const chat = selectActiveChat(r.db, r.mode.activeChatId);
		return {
			panelsVisible: r.mode.panelsVisible,
			activeChat: chat,
			frameIdToScrollTo: r.mode.frameIdToScrollTo,
			centerFramesAsChatGeneratesThem: r.mode.centerFramesAsChatGeneratesThem,
			activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
			authUser: r.mode.authUser,
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			isMobile: selectIsMobile(r.mode),
			pendingChatInput: chat ? r.mode.pendingChatInputs[chat.id] : null,
		};
	}, shallowEqual);

	const entriesLength = activeChat?.entries?.length ?? 0;
	const lastChatMessage =
		activeChat?.entries && activeChat.entries.length > 0
			? activeChat.entries[activeChat.entries.length - 1].message
			: null;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// It is actually quite intricate to get the scroll behavior we want:
	// When the user submits a prompt, we scroll so it's at the top of the screen (leaving space underneath)
	// The space persists -- meaning that we can scroll up and down and it remains
	// Well the space persists until the system's chat responses appear -- they responses gobble up the empty space!
	// If the system's responses don't fill the empty space, a little bit of whitespace is left, the user can
	// scroll up and down and that little bit of whitespace remains.
	//
	// This is the standard but implementing it is pretty intricate
	// To get the scroll view to scroll we need to manually inject some space -- outside of the React loop
	// so it can all happen before the next render. That's why I use useLayoutEffect.
	//
	// I talked to some LLMs who were like "here's the easy trick everyone uses" but it always involved naively
	// calling scrollIntoView without adding empty space. No variation of that worked for me. But doing the math
	// directly here is working very robustly at present -- ss
	//
	// I did have to use this magic number which is needed to avoid adding a bit TOO much space.
	// Perhaps it's needed because padding isn't included in some computations?
	// I didn't dig in too much because it's working well and I don't care much beyond that until is starts to fail (sorry) - ss
	const MAGIC_NUMBER = 18;
	//
	// if last message is a user message, scroll so user message appears at top of chat panel
	useLayoutEffect(() => {
		if (!activeChat) return;
		if (!activeChat.entries) return;
		if (entriesLength === 0) return;

		if (activeChat.entries[entriesLength - 1].kind === 'user') {
			const messages = containerRef.current?.querySelectorAll('.user-message');
			const lastUserMessage = messages?.[messages.length - 1];
			if (lastUserMessage) {
				// make the spacer's height be that of the visible area minus that of the user message
				const visibleAreaHeight = containerRef.current?.clientHeight;
				const userMessageHeight = lastUserMessage.clientHeight;
				if (visibleAreaHeight && spacerRef.current) {
					const H = visibleAreaHeight - userMessageHeight - MAGIC_NUMBER;
					spacerRef.current.style.height = `${H}px`;
					spacerHeight.current = H;
					// store this so we can chip away at it as the last system message grows
					originalSpacerHeight.current = H;
				}
				// since we forcibly sized the spacer, voila! our scrolling works
				if (containerRef.current && lastUserMessage) {
					// Get the offsetTop of the message relative to the container
					const messageOffset = (lastUserMessage as HTMLElement).offsetTop;
					containerRef.current.style.scrollBehavior = 'smooth';
					containerRef.current.scrollTop = messageOffset;
				}
			}
		}
	}, [entriesLength]);
	//
	// whenever the last entry is a system message, eat away at the spacer height as system message grows
	useLayoutEffect(() => {
		if (!activeChat) return;
		if (!activeChat.entries) return;
		if (entriesLength === 0) return;
		if (spacerHeight.current > 0 && activeChat?.entries[entriesLength - 1].kind === 'system') {
			const messages = containerRef.current?.querySelectorAll('.system-message');
			const lastSystemMessage = messages?.[messages.length - 1];
			if (lastSystemMessage) {
				const systemMessageHeight = lastSystemMessage.clientHeight;
				if (systemMessageHeight && spacerRef.current) {
					// remove the height that's been added by the system message
					const newSpacerHeight = Math.max(originalSpacerHeight.current - systemMessageHeight, 0);
					spacerHeight.current = newSpacerHeight;
					spacerRef.current.style.height = `${spacerHeight.current}px`;
				}
			}
		}
	}, [lastChatMessage]);

	// When frameIdToScrollTo changes, scroll to the thumbnail with that frameId (if it exists)
	useEffect(() => {
		if (!frameIdToScrollTo || !containerRef.current) return;

		// Find the element with the matching frame ID
		const frameElement = containerRef.current.querySelector(
			`[data-frame-id="${frameIdToScrollTo}"]`
		);
		if (frameElement) {
			// Get the offsetTop of the frame element relative to the container
			const frameOffset = (frameElement as HTMLElement).offsetTop;
			// Calculate the center position by subtracting half the container height
			const containerHeight = containerRef.current.clientHeight;
			const scrollPosition = frameOffset - containerHeight / 2;

			// Scroll to the frame with smooth behavior
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = scrollPosition;
		}
		// clear out the frameIdToScrollTo so we will get a signal when it's changed again
		dispatch(setFrameIdToScrollTo(null));
	}, [frameIdToScrollTo]);

	// when chat id changes, scroll to the bottom (unless this is an X conversation)
	useEffect(() => {
		// Only proceed if we have scene metadata loaded (to avoid race condition)
		if (containerRef.current && scene?.metadata && !scene.metadata.xConversationId) {
			spacerHeight.current = 0;
			if (spacerRef.current) {
				// if the prior chat left a spacer, clear it
				spacerRef.current.style.height = `${spacerHeight.current}px`;
			}
			// disable smooth scrolling
			containerRef.current.style.scrollBehavior = 'auto';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}
	}, [activeChat?.id, scene?.metadata]);

	// the intent of this hook is to scroll the chat to the bottom when scene loads.
	// in practice, it doesn't always scroll to the right spot because, i believe,
	// the space for thumbnails/etc is not taken into account
	// so i added a generous delay.
	// half a second is a lot but since we're loading and things are popping it doesn't feel too bad.
	// however STILL, even with this delay, on local dev it sometimes scrolls almost (but not all the way) to the bottom
	// let's see how this fares on prod. this here is definitely an improvement from before - ss
	// Skip scrolling to bottom if this is an X conversation
	useEffect(
		() => {
			// Only proceed if we have scene metadata loaded (to avoid race condition)
			if (containerRef.current && scene?.metadata && !scene.metadata.xConversationId) {
				setTimeout(() => {
					if (containerRef.current) {
						containerRef.current.style.scrollBehavior = 'auto';
						containerRef.current.scrollTop = containerRef.current.scrollHeight;
						setIsScrolledToBottom(true);
					}
				}, 500);
			}
		},
		// depend on [containerRef.current] instead of [], because often when scene loads ref is not set
		[containerRef.current, scene?.metadata]
	);

	// Add scroll event listener to track scroll position
	useEffect(
		() => {
			const container = containerRef.current;
			if (!container) {
				return;
			}
			const handleScroll = () => {
				const { scrollTop, scrollHeight, clientHeight } = container;
				// instead of 1 we use 2 as a test to account for floating point precision issues
				const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;

				setIsScrolledToBottom(isAtBottom);
			};

			container.addEventListener('scroll', handleScroll);
			return () => container.removeEventListener('scroll', handleScroll);
		},
		// depend on [containerRef.current] instead of [], because often when scene loads ref is not set
		[containerRef.current]
	);

	// Add new useEffect hook to handle lastChatMessage changes
	useEffect(() => {
		if (!lastChatMessage) return;
		if (!centerFramesAsChatGeneratesThem) return;

		// Get the last <frames> block from the message
		const frameRegex = /<frames>[\s\S]*?<\/frames>/g;
		const matches = lastChatMessage.match(frameRegex);
		if (!matches) return;

		const lastFramesBlock = matches[matches.length - 1];
		const frameIds = parseFramesXml(lastFramesBlock);

		// Compare with current focusedFrameIds
		if (!isEqual(frameIds.sort(), focusedFrameIds.sort())) {
			setFocusedFrameIds(frameIds);

			// Get the frames and calculate their bounding box
			const frames = getFrames(frameIds);
			if (frames.length > 0) {
				// We have a bit of a glitch here. solving it the right way is tricky
				// In practice what happens is that we zoom into just the first frame, instead of the whole set
				//
				// A small hack does a great job of fixing it (for now)
				// The problem is that the frames aren't in the db slice yet when the lastChatMessage updates.
				// Therefore frames.length !== frameIds.length
				// and the when we try to take the bounds of frames, we just get some of them,
				// so we zoom into some (not all) of the frames, in practice just the first one
				//
				// The fix is to assume (as is currently always the case) that the frames are the same size,
				// in a row, separated by FRAME_GAP
				//
				// However this fix does leave the potential for a small bug
				// I haven't seen this bug in practice but theoretically it could happen
				//  - the very last (or nearly last) chage message in a stream has a <frames> block in it
				//  - when we process it here, NONE of the frames are loaded so getFrames(frameIds) returns an empty array
				//  - we don't ever get to this block so we never centerCanvasAroundSceneBoundingBox
				//
				// The consequences of this are minor
				//  - Not zooming in is not the worst thing in the world, not destructire or anything
				// -  Usually there are multiple <frames> blocks in a chat response so the prior frames will have been zoomed into it
				// The fix for this is pretty gnarly
				//  - we have to store the <frames> blocks in some kind of queue that we process later, once the frames are loaded in the db
				//
				// So for now i'm going to do a simple hack and see if more problems arise

				// Instead of doing this:
				//    const bounds = frames.map((frame) => getFrameBounds(frame));
				// Instead take the first frame and use the # of frameIds, along with our frame gneneration
				// rules to generate the bounds.
				// This is robust to the situation where frames.length !== frameIds.length
				const firstFrameBounds = getFrameBounds(frames[0]);
				const bounds = frameIds.map((_, index) => {
					return {
						x: firstFrameBounds.x + index * (FRAME_GAP + firstFrameBounds.width),
						y: firstFrameBounds.y,
						width: firstFrameBounds.width,
						height: firstFrameBounds.height,
					};
				});
				const boundingBox = getBoundingBox(bounds);
				dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox,
						animated: true,
					})
				);
			}
		}
	}, [lastChatMessage]);

	// As messages stream in, update isScrolledToBottom
	useEffect(() => {
		if (lastChatMessage?.length && containerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
			const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
			setIsScrolledToBottom(isAtBottom);
		}
	}, [lastChatMessage?.length]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onJumpToBottom = () => {
		if (containerRef.current) {
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (loading) => {
				// in practice, pendingChatInput should always be set at this point
				if (!activeChat || !pendingChatInput) return;
				dispatch(
					setPendingChatInput({
						chatId: activeChat.id,
						pendingChatInput: {
							...pendingChatInput,
							loadingAttachments: [...(pendingChatInput.loadingAttachments || []), loading],
						},
					})
				);
			},
			onLoadingEnd: (loadingId) => {
				if (!activeChat || !pendingChatInput) return;
				dispatch(
					setPendingChatInput({
						chatId: activeChat.id,
						pendingChatInput: {
							...pendingChatInput,
							loadingAttachments: pendingChatInput.loadingAttachments?.filter(
								(a) => a.uniqueId !== loadingId
							),
						},
					})
				);
			},
			onSuccess: (attachment) => {
				if (!activeChat || !pendingChatInput) return;
				// Check if file already exists
				if (pendingChatInput.attachments?.some((a) => a.storagePath === attachment.storagePath)) {
					return;
				}
				dispatch(
					setPendingChatInput({
						chatId: activeChat.id,
						pendingChatInput: {
							...pendingChatInput,
							attachments: [...(pendingChatInput.attachments || []), attachment],
						},
					})
				);
			},
			onError: (error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[activeChat, dispatch, pendingChatInput]
	);

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);

		if (!!scene?.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		if (!activeChat) return;

		// Use the utility handler
		onFileDropped(fileCallbacks)(e);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderThumbnails = (frameIds: string[]) => {
		if (!frameIds.length) return null;
		return (
			<S.Thumbnails
				key={frameIds.join('-')}
				onWheel={(e) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{frameIds.map((frameId) => (
					<Thumbnail
						key={frameId}
						frameId={frameId}
						size={ThumbnailSize.CHAT}
						data-frame-id={frameId}
						selectOnClick
					/>
				))}
			</S.Thumbnails>
		);
	};

	const renderAttachments = (attachments: Attachment[] | undefined) => {
		if (!attachments?.length) return null;
		return (
			<S.Attachments
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{attachments.map((attachment) => (
					<ChatAttachmentTile
						key={attachment.storagePath}
						attachment={attachment}
						state={ChatAttachmentTileState.REGULAR}
					/>
				))}
			</S.Attachments>
		);
	};

	const renderEntries = (entries: ChatEntry[]) => {
		return entries.map((entry, index) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				const parts = splitMessageWithFrames(entry.message);
				return (
					<S.SystemMessage
						key={index}
						className="system-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{parts.map((part, i) => {
							if (part.type === 'text') {
								const textPart = part as ChatMessageTextPart;
								return (
									<MarkdownText key={i} onContextMenu={onContextMenuInMessage}>
										{textPart.content}
									</MarkdownText>
								);
							} else {
								const framesPart = part as ChatMessageFramesPart;
								return (
									<S.Frames
										key={i}
										onWheel={(e) => {
											e.stopPropagation();
											e.currentTarget.scrollLeft += e.deltaX;
										}}
									>
										{framesPart.frameIds.map((frameId) => (
											<Thumbnail
												key={frameId}
												frameId={frameId}
												size={ThumbnailSize.CHAT}
												selectOnClick
											/>
										))}
									</S.Frames>
								);
							}
						})}
					</S.SystemMessage>
				);
			}
			if (entry.kind === 'user') {
				const userEntry = entry as UserChatEntry;
				return (
					<S.UserMessage
						key={index + 'user'}
						className="user-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{renderAttachments(userEntry.attachments)}
						{renderThumbnails(userEntry.sourceFrameIds)}
						<MarkdownText onContextMenu={onContextMenuInMessage}>{entry.message}</MarkdownText>
					</S.UserMessage>
				);
			}
		});
	};

	const renderStatus = () => {
		if (!activeChat?.metadata?.statusMessage) return null;
		if (!activeChat?.metadata?.status) return null; // just in case...

		const { status, statusMessage } = activeChat.metadata;

		// Only show status for processing or stoppable states
		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<S.StatusMessage isMobile={isMobile}>
				<S.StatusText>{statusMessage}</S.StatusText>
				<Loader width={16} height={16} />
			</S.StatusMessage>
		);
	};

	if (!activeChat) {
		return null;
	}

	return (
		<>
			<S.Base onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
				<S.ScrollingArea ref={containerRef}>
					{renderEntries(activeChat.entries)}
					{renderStatus()}
					<div ref={spacerRef} />
				</S.ScrollingArea>
				{isDraggingFile && (
					<S.DragOverlay>
						<S.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<S.DragOverlayText isMobile={isMobile}>Drop files to attach</S.DragOverlayText>
						</S.DragOverlayContent>
					</S.DragOverlay>
				)}
				{activeChat.entries.length > 0 && <S.Spacer />}
				<S.InputArea>
					{!isScrolledToBottom && (
						<S.JumpButton onClick={onJumpToBottom} isMobile={isMobile}>
							<S.JumpButtonIcon>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={isMobile ? 24 : 18}
									height={isMobile ? 24 : 18}
									color={theme.gray['700']}
								/>
							</S.JumpButtonIcon>
						</S.JumpButton>
					)}
					<ChatInput key={activeChat.id} chat={activeChat} />
				</S.InputArea>
			</S.Base>
		</>
	);
};

export default Conversation;
